import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { useDatasetItem, useUpdateDatasetItem, useDeleteDatasetItem } from '@/hooks/useEvaluator';
import { toast } from '@/hooks/use-toast';

interface DatasetItemDetailProps {
  orgId: string;
  datasetName: string;
  itemId: string;
  onBack: () => void;
}

export function DatasetItemDetail({ orgId, datasetName, itemId, onBack }: DatasetItemDetailProps) {
  const { data: item, isLoading, error } = useDatasetItem(datasetName, itemId, orgId);
  const updateMutation = useUpdateDatasetItem();
  const deleteMutation = useDeleteDatasetItem();

  const [persona, setPersona] = useState('');
  const [goal, setGoal] = useState('');
  const [constraints, setConstraints] = useState('');
  const [initialized, setInitialized] = useState(false);

  // Populate fields once data loads
  useEffect(() => {
    if (item && !initialized) {
      setPersona(item.input?.persona ?? '');
      setGoal(item.input?.goal ?? '');
      setConstraints(item.input?.constraints ?? '');
      setInitialized(true);
    }
  }, [item, initialized]);

  // Determine if there are unsaved changes
  const hasChanges = (() => {
    if (!item || !initialized) return false;
    return (
      persona !== (item.input?.persona ?? '') ||
      goal !== (item.input?.goal ?? '') ||
      constraints !== (item.input?.constraints ?? '')
    );
  })();

  const canSave = hasChanges && !updateMutation.isPending;

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        datasetName,
        itemId,
        organizationId: orgId,
        data: {
          input: { ...item?.input, persona, goal, constraints },
        },
      });
      toast({ title: 'Success', description: 'Item updated.' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update item.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading item...</p>;
  }

  if (error || !item) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <p className="text-sm text-destructive">Failed to load dataset item.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0 mt-0.5">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-heading line-clamp-2">
              {item.input?.persona ?? 'Dataset Item'}
            </h2>
            <p className="text-xs text-muted-foreground">
              {datasetName} &middot; {itemId.slice(0, 8)}...
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete item</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this item from the dataset. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    deleteMutation.mutate(
                      { datasetName, itemId, organizationId: orgId },
                      {
                        onSuccess: () => {
                          toast({ title: 'Deleted', description: 'Item has been deleted.' });
                          onBack();
                        },
                        onError: (err) => {
                          toast({
                            title: 'Error',
                            description: err instanceof Error ? err.message : 'Failed to delete item.',
                            variant: 'destructive',
                          });
                        },
                      },
                    );
                  }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button size="sm" onClick={handleSave} disabled={!canSave}>
            <Save className="mr-2 h-4 w-4" />
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Persona */}
      <div className="space-y-2">
        <Label>Persona</Label>
        <Textarea
          value={persona}
          onChange={(e) => setPersona(e.target.value)}
          className="min-h-[100px] whitespace-pre-wrap break-words"
          placeholder="Describe the persona..."
        />
      </div>

      {/* Goal */}
      <div className="space-y-2">
        <Label>Goal</Label>
        <Textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          className="min-h-[100px] whitespace-pre-wrap break-words"
          placeholder="What is the persona's goal?"
        />
      </div>

      {/* Constraints */}
      <div className="space-y-2">
        <Label>Constraints</Label>
        <Textarea
          value={constraints}
          onChange={(e) => setConstraints(e.target.value)}
          className="min-h-[100px] whitespace-pre-wrap break-words"
          placeholder="Any constraints or boundaries..."
        />
      </div>

      {/* Timestamps */}
      {(item.created_at || item.updated_at) && (
        <div className="flex gap-6 text-xs text-muted-foreground pt-2 border-t">
          {item.created_at && <span>Created: {new Date(item.created_at).toLocaleString()}</span>}
          {item.updated_at && <span>Updated: {new Date(item.updated_at).toLocaleString()}</span>}
        </div>
      )}
    </div>
  );
}
